import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { loggedGet } from './loggedFetch';

function formatCurrencyShort(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function pct(p) {
  if (p == null) return '—';
  return `${(p * 100).toFixed(0)}%`;
}

// A category looks suspicious when one vendor owns ≥85% of it AND that
// vendor's share comes from ≤2 distinct components. That pattern usually
// means the catalog has a single fat product (e.g. "IBM Mainframe Systems")
// monopolizing a broad category bucket — not real market dominance.
function detectCatalogSkew(cat) {
  const all = [
    { vendor: cat.vendor_components ? '[target]' : null, components: cat.vendor_components || [], spend: cat.vendor_spend },
    ...(cat.competitors || []).map(c => ({ vendor: c.vendor, components: c.components || [], spend: c.spend })),
  ].filter(x => x.spend > 0);
  if (all.length === 0) return null;
  const dominant = all.reduce((m, x) => (x.spend > m.spend ? x : m), all[0]);
  const share = cat.category_total > 0 ? dominant.spend / cat.category_total : 0;
  if (share >= 0.85 && dominant.components.length <= 2) {
    return {
      vendor: dominant.vendor === '[target]' ? 'this vendor' : dominant.vendor,
      share,
      components: dominant.components.map(c => c.component_name).filter(Boolean),
    };
  }
  return null;
}

function CompetitiveMap({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown }) {
  const [expanded, setExpanded] = useState({});

  const qs = new URLSearchParams();
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  if (productFamily) qs.set('product_family', productFamily);
  // Phase H.29: department_ids filter for list scoping
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  const suffix = qs.toString() ? `?${qs}` : '';
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/competitors${suffix}`;
  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'competitors', vendor, includeLowConfidence, strict, productFamily, departmentIds, years],
    queryFn: loggedGet('CompetitiveMap:fetch', path),
    enabled: !!vendor,
  });

  if (isLoading) return <div className="vca-section vca-section__loading">Loading competitive map…</div>;
  if (error) return <div className="vca-section vca-section__error">Competitors failed: {error.message}</div>;
  if (!data || (data.by_category || []).length === 0) {
    return (
      <div className="vca-section">
        <h2>Competitive map</h2>
        <p className="vca-section__sub">No category overlap found for {vendor}.</p>
      </div>
    );
  }

  return (
    <div className="vca-section">
      <h2>Competitive map</h2>
      <p className="vca-section__sub">
        For each category {vendor} plays in, share of state-level spend. Each
        competitor row shows the actual product(s) being aggregated under that
        vendor — click the chevron to expand the component breakdown, click
        the row body to drill into POs.
      </p>
      <div className="vca-cat-grid">
        {data.by_category.map(cat => {
          const skew = detectCatalogSkew(cat);
          return (
            <div key={cat.category} className="vca-cat-card">
              <div className="vca-cat-card__head">
                <div className="vca-cat-card__title">{cat.category}</div>
                <div className="vca-cat-card__share">
                  Total {formatCurrencyShort(cat.category_total)}
                </div>
              </div>

              {skew && (
                <div className="vca-cat-skew" title="One vendor + a single component is monopolizing this category. Likely the catalog category is too broad and should be split — see VENDOR_COMPETITIVE_ANALYSIS.md.">
                  <AlertTriangle size={12} />
                  <span>
                    {skew.vendor} owns {pct(skew.share)} but it's all{' '}
                    <strong>{skew.components.join(' + ') || 'one component'}</strong> —
                    category is likely too broad.
                  </span>
                </div>
              )}

              <CategoryRow
                label={vendor}
                spend={cat.vendor_spend}
                share={cat.vendor_share_pct}
                components={cat.vendor_components}
                isVendor
                expanded={!!expanded[`${cat.category}::${vendor}`]}
                onToggle={() => setExpanded(p => ({ ...p, [`${cat.category}::${vendor}`]: !p[`${cat.category}::${vendor}`] }))}
                onDrill={() => onDrilldown({ type: 'vendor', value: vendor })}
              />

              {cat.competitors.slice(0, 8).map(c => (
                <CategoryRow
                  key={c.vendor}
                  label={c.vendor}
                  spend={c.spend}
                  share={c.share_pct}
                  components={c.components}
                  expanded={!!expanded[`${cat.category}::${c.vendor}`]}
                  onToggle={() => setExpanded(p => ({ ...p, [`${cat.category}::${c.vendor}`]: !p[`${cat.category}::${c.vendor}`] }))}
                  onDrill={() => onDrilldown({ type: 'vendor', value: c.vendor })}
                />
              ))}
              {cat.competitors.length > 8 && (
                <div className="vca-section__sub">
                  +{cat.competitors.length - 8} smaller competitors
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryRow({ label, spend, share, components, isVendor, expanded, onToggle, onDrill }) {
  const hasComponents = components && components.length > 0;
  const componentsToShow = expanded ? components : components.slice(0, 1);
  const hiddenCount = (components?.length || 0) - componentsToShow.length;

  return (
    <div>
      <div
        className={`vca-cat-row ${isVendor ? 'is-vendor' : ''}`}
        onClick={onDrill}
        title="Drill into this vendor's POs"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasComponents && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'inline-flex',
                color: 'inherit',
              }}
              aria-label={expanded ? 'Collapse components' : 'Expand components'}
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          )}
          {label}
        </span>
        <span>
          {formatCurrencyShort(spend)} · {pct(share)}
        </span>
      </div>
      {hasComponents && (
        <div className="vca-cat-row__components">
          {componentsToShow.map(c => (
            <div key={c.component_name || '(untagged)'} className="vca-cat-row__component">
              <span title={c.component_name || '(untagged)'}>
                ↳ {c.component_name || '(untagged)'}
              </span>
              <span>{formatCurrencyShort(c.spend)}</span>
            </div>
          ))}
          {!expanded && hiddenCount > 0 && (
            <div className="vca-cat-row__component vca-cat-row__component--more">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', font: 'inherit', padding: 0 }}
              >
                + {hiddenCount} more product{hiddenCount === 1 ? '' : 's'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CompetitiveMap;
